/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from 'react';
import PropTypes from 'prop-types';

import BaseTableComponent from '../../components/BaseTableComponent';

const DISPLAY_NAME = 'BaseTableBody';

export default class BaseTableBody extends BaseTableComponent {
  static displayName = DISPLAY_NAME;

  constructor(props) {
    super({
      childDisplayName: DISPLAY_NAME,
      ...props
    });
  }

  get padedRowLength() {
    const { pageSize, rows, hidePadRows } = this.props;
    const numRows = hidePadRows
      ? Math.min(4, pageSize - rows.length)
      : pageSize - rows.length;
    return rows.length >= pageSize ? 0 : numRows;
  }

  get shouldRenderPadRows() {
    const { variant } = this.props;
    return variant === this.types.DISPLAY;
  }

  clickOnRow = (e, rowIdx) => {
    const { selectRow } = this.props;
    if (e.altKey && selectRow) {
      selectRow(rowIdx);
    }
  };

  /**
   * Render row actions along with table row items for each row
   */
  renderTableRows = () => {
    const { dataTest, rows, tableId, renderTableItem, trProps, selectRow } =
      this.props;

    const renderedRows = rows.map((row, rowIdx) => (
      <tr
        {...trProps()}
        data-test={`${tableId}-${dataTest}-bodyRow-${rowIdx + 1}-cybsTable`}
        // eslint-disable-next-line react/no-array-index-key
        key={`${tableId}-table-body-row-${rowIdx}`}
        /**
         * @see CYBSUI-3411:
         * We have to check if selectRow is truthy first, because the mere
         * presence of this prop causes screen readers to read all cells under
         * this row as "clickable".
         *
         * Edge Case: There's an issue with how React handles removing function
         * props here. If this prop has been added, and then `selectRow` becomes
         * false, the onclick will be replaced with a **noop** instead of null.
         * As a result, this will cause CYBSUI-3411 to occur again.
         */
        onClick={selectRow && (e => this.clickOnRow(e, row.id))}
      >
        {this.renderRowActions(row, row.id)}
        {row &&
          row.fields &&
          row.fields.map((cell, cellIdx) =>
            renderTableItem('td', cell, cellIdx, row.id)
          )}
      </tr>
    ));
    return renderedRows;
  };

  /**
   * Functionality provided by the commented code below has not been solidified in the spec and may not be needed in future iterations of this component.
   */
  renderPadRows = length => {
    let padRows = []; // used when page size is greater than number of rows. need to fill page
    const { tableId, trProps } = this.props;
    if (length > 0) {
      for (let i = 0; i < length; i++) {
        const temp = (
          <tr
            {...trProps()}
            key={`table-row-${tableId}-${i}`}
            className="pad-row"
          >
            {this.renderTdForPadRows(i)}
          </tr>
        );
        padRows = padRows.concat(temp);
      }
    }

    return padRows;
  };

  renderTdForPadRows = (rowIdx) => {
    const { numColumns, tdProps } = this.props;
    const temp = [];
    for (let i = 0; i < numColumns; i++) {
      const padTdProps = tdProps(rowIdx, i);
      temp.push(<td {...padTdProps} data-row="empty" />);
    }

    return temp;
  };

  renderRowActions = (row, rowIdx) => {
    const {
      isFocused,
      onFocus,
      rowActionConfig,
      tdProps,
      renderRowActionBody,
      getRowActionWidthCSS
    } = this.props;
    return this.isNonEmptyArray(rowActionConfig)
      ? rowActionConfig.map((rowAction, cellIdx) => {
          const actionProps = tdProps(rowIdx, cellIdx);
          const handleFocus = () => onFocus(rowIdx + 1, cellIdx);
          const focusable = isFocused(rowIdx + 1, cellIdx);

          return (
            <td
              {...actionProps}
              css={getRowActionWidthCSS(rowAction)}
              onFocus={handleFocus}
              data-focusable={focusable}
              // eslint-disable-next-line react/no-array-index-key
              key={`row-action-${rowIdx}-${cellIdx}`}
            >
              {renderRowActionBody('td', rowAction, row.id, cellIdx, row?.header)}
            </td>
          );
        })
      : null;
  };

  render() {
    const { dataTest, tableId, tBodyProps, shouldRenderEmptyFilterView } = this.props;
    let body = <React.Fragment />;
    let bodyStyle = {};
    if (shouldRenderEmptyFilterView) {
      body = <div style={{ height: '250px', display: 'table-row' }} />;
      bodyStyle = { height: '250px', minHeight: '250px' };
    } else {
      body = (
        <React.Fragment>
          {this.renderTableRows() /* Main table rows for page in view  */}
          {this.shouldRenderPadRows
            ? this.renderPadRows(this.padedRowLength)
            : null}
        </React.Fragment>
      );
    }
    return (
      <tbody
        data-test={`${tableId}-${dataTest}-gridBodyContainer-cybsTable`}
        style={bodyStyle}
        {...tBodyProps()}
      >
        {body}
      </tbody>
    );
  }
}

BaseTableBody.propTypes = {
  /**
   * Page size of gridtable defined in config
   */
  pageSize: PropTypes.number.isRequired,
  /**
   * Props injected into td element
   */
  tdProps: PropTypes.func,
  /**
   * Props injected into tr element
   */
  trProps: PropTypes.func,
  /**
   * Props injected into tbody
   */
  tBodyProps: PropTypes.func,
  /**
   * Virtual column length includes row action config elements
   */
  numColumns: PropTypes.number.isRequired,
  /**
   * Displayed data rows
   */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({ type: PropTypes.string.isRequired })
      ).isRequired,
      header: PropTypes.string
    })
  ).isRequired,
  /**
   * Event listener for checking current focused elements
   */
  isFocused: PropTypes.func.isRequired,
  /**
   * Event listener for handling focusing and selection via keyboard
   */
  onKeyDown: PropTypes.func.isRequired,
  /**
   * Event listener for focusing elements
   */
  onFocus: PropTypes.func.isRequired,
  /**
   * Renderer function for row action table elements. Specifically table data
   */
  renderRowActionBody: PropTypes.func.isRequired,
  /**
   * Flag for rendering empty filter view
   */
  shouldRenderEmptyFilterView: PropTypes.bool.isRequired,
  /**
   * Main header object configuration
   */
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      filter: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }),
      width: PropTypes.number,
      sortable: PropTypes.bool
    })
  ).isRequired,
  /**
   * Used to render row action flags and row actions e.g. buttons and row selection and any future flags to render
   */
  rowActionConfig: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.object // options are props passed into button for the row action
  }).isRequired,

  /**
   * Localization
   */
  i18n: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  /**
   * function to be called when a row is clicked on for select (alt+click)
   */
  selectRow: PropTypes.func
};

BaseTableBody.defaultProps = {
  tdProps: () => {},
  trProps: () => {},
  tBodyProps: () => {},
  i18n: {
    title: 'No Results',
    subtitle: 'Please change filters to reset results'
  },
  selectRow: undefined
};
